/* biome-ignore-all lint/suspicious/noExplicitAny: legacy data structure */
import { useState } from "react"
import { useQuery } from "@tanstack/react-query"
import { Modal, Table, Input, Tag, Typography, Button, Space, type TableProps } from "antd"
import { FolderOutlined, SearchOutlined, SoundOutlined, ReloadOutlined } from "@ant-design/icons"
import type React from "react"
import { vocabularyService } from "@/services/dictionary/vocabularyApi"
import { formatDateTime } from "@/utils/dateUtils"

import SrsFormBadges from "@/components/srs/SrsFormBadges"

const { Text } = Typography

interface UserSetVocabulariesModalProps {
  open: boolean
  record: any | null
  onClose: () => void
}

export const UserSetVocabulariesModal: React.FC<UserSetVocabulariesModalProps> = ({
  open,
  record,
  onClose,
}) => {
  const [page, setPage] = useState<number>(1)
  const [pageSize, setPageSize] = useState<number>(20)
  const [searchText, setSearchText] = useState<string>("")
  const [audioUrl, setAudioUrl] = useState<string | null>(null)

  const {
    data: result,
    isFetching,
    refetch,
  } = useQuery({
    queryKey: ["user_set_vocabularies", record?.id, page, pageSize, searchText],
    queryFn: () => {
      if (!record?.id) return null
      return vocabularyService.list({
        page,
        limit: pageSize,
        set_id: record.id,
        search: searchText || undefined,
      })
    },
    enabled: !!record?.id && open,
  })

  const playAudio = (url?: string) => {
    if (!url) return
    const audio = new Audio(url)
    audio.play().catch(() => {})
  }

  const items = result?.data || []
  const total = result?.paginate?.total || 0

  const columns: TableProps<any>["columns"] = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      width: 80,
      align: "center",
      render: (id: number) => <span className="font-semibold">{id}</span>,
    },
    {
      title: "Từ vựng",
      key: "text",
      width: 170,
      render: (_: any, item: any) => (
        <div className="flex flex-col gap-0.5">
          <div className="flex items-center gap-1.5 font-bold text-blue-600 text-sm">
            <span>{item.text}</span>
            {item.audio && (
              <Button
                type="text"
                size="small"
                icon={<SoundOutlined className="text-blue-500" />}
                onClick={() => playAudio(item.audio)}
                className="h-5 w-5 p-0"
              />
            )}
          </div>
          {item.ipa && <span className="text-gray-500 font-mono text-xs">/{item.ipa}/</span>}
        </div>
      ),
    },
    {
      title: "Loại từ",
      dataIndex: "part_of_speech",
      key: "part_of_speech",
      width: 90,
      align: "center",
      render: (pos: string) =>
        pos ? (
          <Tag color="cyan" className="font-medium text-[11px] uppercase">
            {pos}
          </Tag>
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
    {
      title: "Nghĩa Tiếng Việt",
      dataIndex: "meaning",
      key: "meaning",
      width: 220,
      render: (meaning: string) => (
        <span className="font-medium text-gray-800 text-xs leading-relaxed">{meaning || "—"}</span>
      ),
    },
    {
      title: "Ví dụ & Dịch nghĩa",
      key: "example",
      render: (_: any, item: any) => {
        if (!item.example && !item.example_meaning) {
          return <Text type="secondary">—</Text>
        }
        return (
          <div className="flex flex-col gap-0.5 text-xs">
            {item.example && <span className="italic text-gray-700">{item.example}</span>}
            {item.example_meaning && (
              <span className="text-gray-500">{item.example_meaning}</span>
            )}
          </div>
        )
      },
    },
    {
      title: "Lịch sử SRS (Form)",
      key: "srs_history",
      width: 155,
      align: "center",
      render: (_: any, item: any) => <SrsFormBadges srsProgress={item.srs_progress} />,
    },
    {
      title: "Ngày lưu",
      dataIndex: "created_at",
      key: "created_at",
      width: 120,
      align: "center",
      render: (val: string) => (val ? formatDateTime(val, "dd/mm/yy hh:mm") : "—"),
    },
  ]

  return (
    <Modal
      title={
        <div className="flex items-center justify-between pr-8">
          <div className="flex items-center gap-2">
            <FolderOutlined style={{ color: record?.color || "#1677ff" }} className="text-lg" />
            <span className="font-bold text-gray-800 text-base">{record?.title}</span>
            <Tag color="blue">#{record?.id}</Tag>
            {record?.user && (
              <span className="text-xs text-gray-500 font-normal">
                (Tạo bởi: <strong>{record.user.name || record.user.username}</strong>)
              </span>
            )}
          </div>
        </div>
      }
      open={open}
      onCancel={onClose}
      footer={[
        <Button key="close" type="primary" onClick={onClose}>
          Đóng
        </Button>,
      ]}
      width={1050}
      destroyOnClose
    >
      <div className="flex flex-col gap-3 py-2">
        <div className="flex items-center justify-between gap-4">
          <Input
            placeholder="Tìm kiếm từ vựng trong bộ này..."
            prefix={<SearchOutlined className="text-gray-400" />}
            allowClear
            value={searchText}
            onChange={(e) => {
              setSearchText(e.target.value)
              setPage(1)
            }}
            className="max-w-[320px]"
          />
          <div className="flex items-center gap-2">
            <Text type="secondary" className="text-xs">
              Tổng số: <strong className="text-gray-700">{total}</strong> từ vựng
            </Text>
            <Button
              size="small"
              icon={<ReloadOutlined spin={isFetching} />}
              onClick={() => refetch()}
              loading={isFetching}
            >
              Làm mới
            </Button>
          </div>
        </div>

        <Table
          bordered
          columns={columns}
          dataSource={items}
          rowKey="id"
          loading={isFetching}
          size="small"
          pagination={{
            current: page,
            pageSize,
            total,
            showSizeChanger: true,
            pageSizeOptions: ["10", "20", "50", "100"],
            onChange: (p, ps) => {
              setPage(p)
              setPageSize(ps)
            },
          }}
          scroll={{ y: 400 }}
        />
      </div>
    </Modal>
  )
}

export default UserSetVocabulariesModal
